import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Settings2 } from "lucide-react";

/**
 * ApportionmentConfig
 * ────────────────────
 * Lets Owner/CFO/Accountant pick the basis on which HO indirect expenses are
 * spread across non-HO segments in Segment MIS. When `basis == "custom"`, we
 * capture a numeric weight per non-HO segment (normalised at query time).
 */
export default function ApportionmentConfig({ dimension = "business" }) {
  const [options, setOptions] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [segments, setSegments] = useState([]);
  const [saving, setSaving] = useState(false);

  const basisKey  = `${dimension}_basis`;
  const weightsKey = `${dimension}_custom_weights`;

  useEffect(() => {
    (async () => {
      try {
        const [opts, current, segs] = await Promise.all([
          api.get("/mis/basis-options"),
          api.get("/mis/apportionment"),
          api.get(`/segments/${dimension}`),
        ]);
        setOptions(opts.data.options || []);
        setCfg(current.data);
        setSegments((segs.data || []).filter(s => !s.is_head_office));
      } catch (e) { console.error(e); }
    })();
  }, [dimension]);

  if (!cfg) return null;

  const basis = cfg[basisKey] || "revenue";
  const weights = cfg[weightsKey] || {};

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/mis/apportionment", {
        [basisKey]: basis,
        [weightsKey]: weights,
      });
      toast.success("Apportionment saved — reload MIS Pack to see the impact");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const chosen = options.find(o => o.value === basis);

  return (
    <Card className="p-4 rounded-sm border-l-4 border-accent" data-testid={`apportionment-${dimension}`}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-accent mb-2">
        <Settings2 className="w-3.5 h-3.5" /> Indirect cost apportionment · {dimension}
      </div>
      <div className="grid md:grid-cols-3 gap-3 items-end">
        <div>
          <Label className="text-xs">Basis</Label>
          <Select value={basis} onValueChange={(v) => setCfg({ ...cfg, [basisKey]: v })}>
            <SelectTrigger className="rounded-sm mt-1" data-testid={`app-basis-${dimension}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {chosen && <div className="text-[10px] text-muted-foreground mt-1">{chosen.description}</div>}
        </div>
        <div className="md:col-span-2">
          {basis === "custom" && (
            <div>
              <Label className="text-xs">Custom weights per segment (any positive number — will be normalised)</Label>
              <div className="grid sm:grid-cols-2 gap-2 mt-1 max-h-40 overflow-auto">
                {segments.map((s) => (
                  <div key={s.id} className="flex items-center gap-2">
                    <span className="font-mono text-[10px] w-14 truncate">{s.code}</span>
                    <span className="text-xs flex-1 truncate">{s.name}</span>
                    <Input type="number" step="0.1" min="0"
                             value={weights[s.id] ?? ""}
                             onChange={(e) => setCfg({
                                ...cfg,
                                [weightsKey]: { ...weights, [s.id]: Number(e.target.value) },
                             })}
                             className="rounded-sm h-8 w-24 font-mono text-right"
                             placeholder="0" data-testid={`app-weight-${s.code}`} />
                  </div>
                ))}
                {segments.length === 0 && (
                  <div className="text-xs italic text-muted-foreground">
                    Add non-HO segments first in Segments module.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <Button onClick={save} disabled={saving} size="sm"
                className="mt-3 rounded-sm bg-accent hover:bg-accent/90 text-white"
                data-testid={`app-save-${dimension}`}>
        {saving ? "Saving…" : "Save apportionment"}
      </Button>
    </Card>
  );
}
